import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canOpenProjectMonitoring, isStaffRole } from '../../utils/projectMonitoringAccess';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Home as HomeIcon,
  CalendarMonth as CalendarIcon,
  Receipt as ReceiptIcon,
  QrCode2 as QrCodeIcon,
  DescriptionOutlined as PayslipIcon,
  Assignment as ReportIcon,
  Payments as PaymentsIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';

interface EmployeeNavListProps {
  isExpanded: boolean;
  navBtnSx: (selected: boolean, isSubItem?: boolean) => object;
  iconSx: (small?: boolean) => object;
}

const EmployeeNavList: React.FC<EmployeeNavListProps> = ({ isExpanded, navBtnSx, iconSx }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const items: { label: string; icon: React.ReactNode; path: string; exact?: boolean }[] = [
    { label: 'Home', icon: <HomeIcon />, path: '/employee', exact: true },
    { label: 'Clock In/Out', icon: <QrCodeIcon />, path: '/employee/clock' },
    { label: 'Daily Time Record', icon: <CalendarIcon />, path: '/employee/dtr' },
    { label: 'Payslips', icon: <PayslipIcon />, path: '/employee/payslips' },
    { label: 'Service Report', icon: <ReportIcon />, path: '/employee/service-report' },
    { label: 'Liquidation', icon: <ReceiptIcon />, path: '/employee/liquidation-form' },
    { label: 'Cash Advance', icon: <PaymentsIcon />, path: '/employee/ca-form' },
  ];
  // Admins switch workspaces from the header; ticked staff get a link here.
  if (isStaffRole(user) && canOpenProjectMonitoring(user)) {
    items.push({ label: 'Project Monitoring', icon: <DashboardIcon />, path: '/dashboard' });
  }

  return (
    <List sx={{ px: 1 }}>
      {items.map((item) => {
        const selected = item.exact
          ? location.pathname === item.path
          : location.pathname.startsWith(item.path);
        return (
          <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
            <Tooltip title={isExpanded ? '' : item.label} placement="right" arrow>
              <ListItemButton
                selected={selected}
                onClick={() => navigate(item.path)}
                sx={navBtnSx(selected)}
              >
                <ListItemIcon sx={iconSx()}>{item.icon}</ListItemIcon>
                {isExpanded && (
                  <ListItemText primary={item.label} sx={{ color: 'white' }} />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        );
      })}
    </List>
  );
};

export default EmployeeNavList;
